/**
 * Pelacak riwayat navigasi klien (App Router) — menjawab dua hal yang tak bisa
 * dilihat komponen dari dalam React:
 *
 *  1. "Halaman ini tiba lewat tombol Kembali/Maju (traversal riwayat), bukan
 *     tap link baru?" — beranda hanya memulihkan posisi feed pada kasus itu
 *     (`homeFeedMemory.ts`); tap "Beranda" di bottom-nav tetap dari atas.
 *  2. "Ada entri riwayat milik situs ini di belakang?" — tombol panah Kembali
 *     di halaman produk memakai `history.back()` bila ada (posisi beranda
 *     pulih, tanpa menumpuk entri "/" baru); bila tidak (link produk dibuka
 *     langsung dari WhatsApp/iklan) ia tetap menaut ke "/" — `history.back()`
 *     di sana justru melempar pembeli KELUAR situs.
 *
 * Traversal navigasi klien dideteksi dari `popstate` (universal); muat-penuh
 * (tab dibuang Android lalu dibuka lagi, reload manual) dari tipe
 * `PerformanceNavigationTiming` dokumen ini — hanya pada pemeriksaan PERTAMA
 * setelah muat, dan hanya bila URL-nya memang URL dokumen itu (dokumen
 * `/product/x` yang lalu di-tap "Beranda" bukan traversal ke "/").
 *
 * Kedalaman riwayat: Navigation API (`navigation.currentEntry.index` hanya
 * menghitung entri same-origin yang bersambung — tepat) bila tersedia;
 * fallback indeks yang distempel sendiri ke `history.state`. Next
 * mempertahankan kunci asing di sana (`copyNextJsInternalHistoryState`) dan
 * mengembalikannya saat traversal (`preserveCustomHistoryState`), tetapi
 * navigasi push/replace membuat state baru tanpa kunci kita → dihitung entri
 * baru (indeks +1). `router.replace` ikut +1 (over-count) — sengaja:
 * under-count membuat panah Kembali mendorong "/" pada alur paling umum
 * (beranda → produk → kembali → produk lain, yang memangkas entri maju),
 * sedangkan over-count hanya salah pada deep-link yang langsung di-replace,
 * alur yang tak ada di storefront.
 *
 * Fungsi keputusan murni (bagian atas) diuji langsung; bagian bawah memegang
 * state terikat browser dan dipasang sekali lewat `HistoryNavTracker`.
 */

/** Kunci indeks riwayat di `history.state` (di samping kunci internal Next). */
export const HISTORY_INDEX_KEY = "__mhHistoryIndex";

/** Usia maksimum sinyal popstate — fetch RSC di 3G masih jauh di bawah ini. */
export const TRAVERSAL_MAX_AGE_MS = 30_000;

export type TraversalMark = { key: string; at: number };

/** Pathname + search (tanpa hash) — pembanding "lokasi yang sama" lintas sumber. */
export function locationKey(href: string, base = "http://localhost"): string {
  try {
    const url = new URL(href, base);
    return `${url.pathname}${url.search}`;
  } catch {
    return href;
  }
}

/** Apakah popstate terakhir menuju lokasi ini dan masih segar? */
export function isTraversalArrival(
  mark: TraversalMark | null,
  currentKey: string,
  now: number,
  maxAgeMs: number = TRAVERSAL_MAX_AGE_MS
): boolean {
  if (!mark || mark.key !== currentKey) return false;
  const age = now - mark.at;
  return age >= 0 && age <= maxAgeMs;
}

/** Muat-penuh yang setara traversal: back_forward / reload ke URL dokumen ini. */
export function isTraversalPageLoad(
  navigationType: unknown,
  documentKey: string | null,
  currentKey: string
): boolean {
  if (navigationType !== "back_forward" && navigationType !== "reload") {
    return false;
  }
  return documentKey !== null && documentKey === currentKey;
}

/** Indeks yang pernah distempel di `history.state`, atau null bila entri baru. */
export function historyIndexFromState(state: unknown): number | null {
  if (!state || typeof state !== "object") return null;
  const value = (state as Record<string, unknown>)[HISTORY_INDEX_KEY];
  return typeof value === "number" && Number.isInteger(value) && value >= 0
    ? value
    : null;
}

/** Entri berstempel memakai indeksnya; entri baru = satu di atas entri terakhir. */
export function nextHistoryIndex(
  stateIndex: number | null,
  lastIndex: number
): number {
  return stateIndex ?? lastIndex + 1;
}

/** Klik kiri polos (tanpa modifier & belum di-preventDefault) — layak di-intersep. */
export function isPlainClick(event: {
  button: number;
  metaKey: boolean;
  ctrlKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
  defaultPrevented: boolean;
}): boolean {
  return (
    !event.defaultPrevented &&
    event.button === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey
  );
}

// ─── State terikat browser ───────────────────────────────────────────────────

let traversalMark: TraversalMark | null = null;
let lastIndex = -1;
let pageLoadChecked = false;
let uninstall: (() => void) | null = null;

/**
 * Pasang listener `popstate` (idempoten). Mengembalikan fungsi pelepas —
 * cocok sebagai cleanup effect (StrictMode dev memasang-lepas-pasang).
 */
export function installHistoryNavTracker(): () => void {
  if (typeof window === "undefined") return () => {};
  if (uninstall) return uninstall;
  const onPopState = () => {
    // `location` sudah menunjuk URL tujuan saat popstate dipancarkan.
    traversalMark = { key: locationKey(window.location.href), at: Date.now() };
  };
  window.addEventListener("popstate", onPopState);
  const remove = () => {
    window.removeEventListener("popstate", onPopState);
    if (uninstall === remove) uninstall = null;
  };
  uninstall = remove;
  return remove;
}

function documentNavigationEntry(): { type: unknown; name: string } | null {
  try {
    if (
      typeof performance === "undefined" ||
      typeof performance.getEntriesByType !== "function"
    ) {
      return null;
    }
    const entries = performance.getEntriesByType("navigation");
    if (!entries.length) return null;
    const entry = entries[0] as PerformanceEntry & { type?: unknown };
    return { type: entry.type, name: entry.name };
  } catch {
    return null;
  }
}

/**
 * Apakah mount halaman di `href` ini hasil traversal riwayat? Sinyal popstate
 * dikonsumsi (sekali pakai); pemeriksaan tipe muat-penuh hanya pada panggilan
 * pertama setelah dokumen dimuat.
 */
export function arrivedByHistoryTraversal(
  href: string,
  now: number = Date.now()
): boolean {
  const key = locationKey(href);
  if (isTraversalArrival(traversalMark, key, now)) {
    traversalMark = null;
    return true;
  }
  if (!pageLoadChecked) {
    pageLoadChecked = true;
    const entry = documentNavigationEntry();
    if (
      entry &&
      isTraversalPageLoad(entry.type, entry.name ? locationKey(entry.name) : null, key)
    ) {
      return true;
    }
  }
  return false;
}

/**
 * Stempel indeks riwayat entri aktif (dipanggil tiap pathname/search berganti).
 * Entri yang sudah berstempel (traversal kembali/maju, reload) memakai
 * indeksnya; entri baru = indeks terakhir + 1 lalu ditulis ke `history.state`
 * TANPA argumen URL (URL "" akan membuang hash).
 */
export function syncHistoryIndex(): number {
  if (typeof window === "undefined") return 0;
  const state: unknown = window.history.state;
  const existing = historyIndexFromState(state);
  const index = nextHistoryIndex(existing, lastIndex);
  if (existing === null) {
    try {
      const base = state && typeof state === "object" ? state : {};
      window.history.replaceState({ ...base, [HISTORY_INDEX_KEY]: index }, "");
    } catch {
      // Safari membatasi pushState/replaceState (100×/30 dtk) — entri ini
      // sekadar tak berstempel; panah Kembali jatuh ke href "/" seperti dulu.
    }
  }
  lastIndex = index;
  return index;
}

type NavigationApiLike = { currentEntry?: { index?: unknown } | null };

/** Ada entri riwayat same-origin di belakang entri aktif? */
export function canGoBackInApp(): boolean {
  if (typeof window === "undefined") return false;
  const nav = (window as unknown as { navigation?: NavigationApiLike }).navigation;
  const index = nav?.currentEntry?.index;
  if (typeof index === "number" && index >= 0) return index > 0;
  return (historyIndexFromState(window.history?.state) ?? 0) > 0;
}

/** Reset state modul — hanya untuk pengujian. */
export function resetHistoryNavForTests(): void {
  traversalMark = null;
  lastIndex = -1;
  pageLoadChecked = false;
  uninstall?.();
  uninstall = null;
}
